import { Capacitor } from '@capacitor/core'
import { fcmTokenService, type FCMTokenData } from './FCMTokenService'
import { auth, db, functions } from '@/config/firebase'
import { doc, setDoc, deleteDoc, collection, getDocs } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'

// Type definitions for Capacitor push notifications
interface Token {
  value: string
}

interface PushNotificationSchema {
  title?: string
  body?: string
  id?: string
  badge?: number
  data?: any
}

interface ActionPerformed {
  actionId: string
  notification: PushNotificationSchema
}

export interface PushNotificationPayload {
  title: string
  body: string
  data?: Record<string, any>
  badge?: number
  sound?: string
  clickAction?: string
  tag?: string
  requireInteraction?: boolean
  actions?: Array<{
    action: string
    title: string
    icon?: string
  }>
  schedule?: {
    at: Date
    repeats?: boolean
    every?: 'day' | 'week' | 'month'
  }
}

export interface NotificationChannel {
  id: string
  name: string
  description?: string
  importance: 1 | 2 | 3 | 4 | 5
  visibility: -1 | 0 | 1
  sound?: string
  vibration?: boolean
  badge?: boolean
}

class UnifiedPushNotificationService {
  private isInitialized = false
  private platform: 'web' | 'ios' | 'android' = 'web'
  private listeners: Array<() => void> = []

  async initialize(): Promise<void> {
    if (this.isInitialized) return

    try {
      // Detect platform
      this.platform = this.detectPlatform()
      console.log(`Initializing push notifications for platform: ${this.platform}`)

      if (this.platform === 'web') {
        // Initialize FCM for web
        await fcmTokenService.initialize()
      } else {
        // Initialize Capacitor Push Notifications for native
        await this.initializeNativePush()
      }

      this.isInitialized = true
      console.log('Push notification service initialized successfully')
    } catch (error) {
      console.error('Failed to initialize push notifications:', error)
      throw error
    }
  }

  private detectPlatform(): 'web' | 'ios' | 'android' {
    if (!Capacitor.isNativePlatform()) {
      return 'web'
    }
    
    const platform = Capacitor.getPlatform()
    return platform as 'ios' | 'android'
  }

  private async initializeNativePush(): Promise<void> {
    try {
      const { PushNotifications } = await import('@capacitor/push-notifications')
      
      // Request permissions
      let permStatus = await PushNotifications.checkPermissions()
      
      if (permStatus.receive === 'prompt') {
        permStatus = await PushNotifications.requestPermissions()
      }

      if (permStatus.receive !== 'granted') {
        throw new Error('Push notification permission denied')
      }

      // Register for push notifications
      await PushNotifications.register()

      // Setup notification channels for Android
      if (this.platform === 'android') {
        await this.createDefaultChannels()
      }

      // Add listeners
      this.addNativeListeners()
    } catch (error) {
      console.error('Failed to initialize native push notifications:', error)
      throw error
    }
  }

  private async createDefaultChannels(): Promise<void> {
    try {
      const { PushNotifications } = await import('@capacitor/push-notifications')
      
      const channels: NotificationChannel[] = [
        {
          id: 'reminders',
          name: 'Challenge Reminders',
          description: 'Daily challenge reminders',
          importance: 4,
          visibility: 1,
          vibration: true,
          badge: true
        },
        {
          id: 'achievements',
          name: 'Achievements',
          description: 'Achievement notifications',
          importance: 5,
          visibility: 1,
          sound: 'achievement.wav',
          vibration: true,
          badge: true
        },
        {
          id: 'social',
          name: 'Social Updates',
          description: 'Friend activities and updates',
          importance: 3,
          visibility: 0,
          vibration: true,
          badge: true
        }
      ]

      for (const channel of channels) {
        await PushNotifications.createChannel(channel)
      }
    } catch (error) {
      console.error('Failed to create notification channels:', error)
    }
  }

  private async addNativeListeners(): Promise<void> {
    try {
      const { PushNotifications } = await import('@capacitor/push-notifications')
      
      // Registration success
      const registrationListener = PushNotifications.addListener('registration', async (token: Token) => {
        console.log('Push registration success, token:', token.value.substring(0, 20) + '...')
        await this.saveNativeToken(token.value)
      })
      this.listeners.push(() => registrationListener.remove())

      // Registration error
      const errorListener = PushNotifications.addListener('registrationError', (error: any) => {
        console.error('Push registration error:', error)
      })
      this.listeners.push(() => errorListener.remove())

      // Foreground notifications
      const notificationListener = PushNotifications.addListener(
        'pushNotificationReceived',
        (notification: PushNotificationSchema) => {
          console.log('Push notification received in foreground:', notification)
          this.handleForegroundNotification(notification)
        }
      )
      this.listeners.push(() => notificationListener.remove())

      // Notification action performed
      const actionListener = PushNotifications.addListener(
        'pushNotificationActionPerformed',
        (notification: ActionPerformed) => {
          console.log('Push notification action performed:', notification)
          this.handleNotificationAction(notification)
        }
      )
      this.listeners.push(() => actionListener.remove())
    } catch (error) {
      console.error('Failed to add native listeners:', error)
    }
  }

  private async saveNativeToken(token: string): Promise<void> {
    try {
      const currentUser = auth.currentUser
      if (!currentUser) {
        console.log('No authenticated user to save token for')
        return
      }

      const tokenData: FCMTokenData = {
        token,
        platform: this.platform as 'ios' | 'android',
        deviceId: await this.getDeviceId(),
        lastUpdated: new Date().toISOString(),
        userId: currentUser.uid
      }

      // Save to user's FCM tokens subcollection
      await setDoc(
        doc(db, 'users', currentUser.uid, 'fcmTokens', this.platform),
        tokenData
      )

      console.log(`${this.platform} push token saved to Firestore`)
    } catch (error) {
      console.error('Error saving native token:', error)
    }
  }

  private handleForegroundNotification(notification: PushNotificationSchema): void {
    // Dispatch custom event for in-app handling
    const event = new CustomEvent('push-notification-received', {
      detail: {
        title: notification.title || 'Notification',
        body: notification.body || '',
        data: notification.data || {},
        platform: this.platform,
        id: notification.id,
        badge: notification.badge
      }
    })

    window.dispatchEvent(event)
  }

  private handleNotificationAction(action: ActionPerformed): void {
    const { actionId, notification } = action

    // Handle different actions
    switch (actionId) {
      case 'view':
        if (notification.data?.challengeId) {
          window.location.href = `/challenges/${notification.data.challengeId}`
        }
        break
      
      case 'complete':
        // Handle quick complete action
        this.handleQuickComplete(notification.data?.challengeId)
        break
      
      case 'snooze':
        // Handle snooze action
        this.handleSnooze(notification.data)
        break
      
      default:
        // Handle tap (default action)
        if (notification.data?.url) {
          window.location.href = notification.data.url
        }
    }
  }

  private async handleQuickComplete(challengeId?: string): Promise<void> {
    if (!challengeId) return

    try {
      // Call cloud function to mark challenge as complete
      const completeChallenge = httpsCallable(functions, 'completeChallenge')
      await completeChallenge({ challengeId })
      
      console.log('Challenge marked as complete via notification action')
    } catch (error) {
      console.error('Failed to complete challenge:', error)
    }
  }

  private async handleSnooze(data: any): Promise<void> {
    if (!data?.notificationId) return

    try {
      // Schedule a new notification for 1 hour later
      const snoozeTime = new Date(Date.now() + 60 * 60 * 1000) // 1 hour
      
      await this.scheduleNotification({
        title: data.title || 'Reminder',
        body: data.body || 'Snoozed reminder',
        data: { ...data, snoozed: true },
        schedule: { at: snoozeTime }
      })
      
      console.log('Notification snoozed for 1 hour')
    } catch (error) {
      console.error('Failed to snooze notification:', error)
    }
  }

  async requestPermissions(): Promise<boolean> {
    try {
      if (this.platform === 'web') {
        const permission = await Notification.requestPermission()
        return permission === 'granted'
      } else {
        try {
          const { PushNotifications } = await import('@capacitor/push-notifications')
          const result = await PushNotifications.requestPermissions()
          return result.receive === 'granted'
        } catch (error) {
          console.error('Failed to import PushNotifications:', error)
          return false
        }
      }
    } catch (error) {
      console.error('Failed to request permissions:', error)
      return false
    }
  }

  async getPermissionStatus(): Promise<'granted' | 'denied' | 'prompt'> {
    if (this.platform === 'web') {
      return Notification.permission
    } else {
      try {
        const { PushNotifications } = await import('@capacitor/push-notifications')
        const result = await PushNotifications.checkPermissions()
        return result.receive
      } catch (error) {
        console.error('Failed to check permissions:', error)
        return 'denied'
      }
    }
  }

  async scheduleNotification(payload: PushNotificationPayload): Promise<string | null> {
    try {
      const currentUser = auth.currentUser
      if (!currentUser) {
        console.log('No authenticated user')
        return null
      }

      // For scheduled notifications, we need to use a cloud function
      const scheduleNotification = httpsCallable(functions, 'scheduleNotification')
      
      const result = await scheduleNotification({
        userId: currentUser.uid,
        notification: {
          title: payload.title,
          body: payload.body,
          data: payload.data || {},
          scheduledTime: payload.schedule?.at?.toISOString(),
          repeats: payload.schedule?.repeats,
          repeatInterval: payload.schedule?.every,
          channelId: payload.data?.channelId || 'reminders',
          actions: payload.actions,
          badge: payload.badge,
          sound: payload.sound,
          clickAction: payload.clickAction,
          tag: payload.tag,
          requireInteraction: payload.requireInteraction
        }
      })

      const notificationId = (result.data as any).notificationId
      console.log('Notification scheduled with ID:', notificationId)
      
      return notificationId
    } catch (error) {
      console.error('Failed to schedule notification:', error)
      return null
    }
  }

  async cancelScheduledNotification(notificationId: string): Promise<boolean> {
    try {
      const cancelNotification = httpsCallable(functions, 'cancelScheduledNotification')
      
      await cancelNotification({ notificationId })
      console.log('Scheduled notification cancelled:', notificationId)
      
      return true
    } catch (error) {
      console.error('Failed to cancel notification:', error)
      return false
    }
  }

  async sendInstantNotification(payload: PushNotificationPayload): Promise<boolean> {
    try {
      const currentUser = auth.currentUser
      if (!currentUser) {
        console.log('No authenticated user')
        return false
      }

      // Send notification immediately via cloud function
      const sendNotification = httpsCallable(functions, 'sendNotification')
      
      await sendNotification({
        userId: currentUser.uid,
        notification: {
          title: payload.title,
          body: payload.body,
          data: payload.data || {},
          channelId: payload.data?.channelId || 'reminders',
          actions: payload.actions,
          badge: payload.badge,
          sound: payload.sound,
          clickAction: payload.clickAction,
          tag: payload.tag,
          requireInteraction: payload.requireInteraction
        }
      })

      console.log('Instant notification sent successfully')
      return true
    } catch (error) {
      console.error('Failed to send instant notification:', error)
      return false
    }
  }

  async getAllTokens(): Promise<FCMTokenData[]> {
    try {
      const currentUser = auth.currentUser
      if (!currentUser) return []

      const tokensRef = collection(db, 'users', currentUser.uid, 'fcmTokens')
      const snapshot = await getDocs(tokensRef)
      
      return snapshot.docs.map(doc => doc.data() as FCMTokenData)
    } catch (error) {
      console.error('Failed to get tokens:', error)
      return []
    }
  }

  async deleteToken(platform: 'web' | 'ios' | 'android'): Promise<void> {
    try {
      const currentUser = auth.currentUser
      if (!currentUser) return

      await deleteDoc(doc(db, 'users', currentUser.uid, 'fcmTokens', platform))
      console.log(`Deleted ${platform} token`)
    } catch (error) {
      console.error('Failed to delete token:', error)
    }
  }

  async deleteAllTokens(): Promise<void> {
    try {
      const tokens = await this.getAllTokens()
      
      await Promise.all(
        tokens.map(token => this.deleteToken(token.platform))
      )
      
      console.log('All tokens deleted')
    } catch (error) {
      console.error('Failed to delete all tokens:', error)
    }
  }

  private async getDeviceId(): Promise<string> {
    if (this.platform === 'web') {
      let deviceId = localStorage.getItem('fcm_device_id')
      if (!deviceId) {
        deviceId = `web_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
        localStorage.setItem('fcm_device_id', deviceId)
      }
      return deviceId
    }
    
    // For native platforms, use the Device plugin to get a unique device identifier
    try {
      const { Device } = await import('@capacitor/device')
      const info = await Device.getId()
      return info.identifier || `${this.platform}_${Date.now()}`
    } catch (error) {
      console.warn('Device plugin not available, using fallback ID:', error)
      // Fallback to localStorage-based ID if Device plugin fails
      let deviceId = localStorage.getItem(`${this.platform}_device_id`)
      if (!deviceId) {
        deviceId = `${this.platform}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
        localStorage.setItem(`${this.platform}_device_id`, deviceId)
      }
      return deviceId
    }
  }

  cleanup(): void {
    // Remove all listeners
    this.listeners.forEach(remove => remove())
    this.listeners = []
    
    // Cleanup FCM service if web
    if (this.platform === 'web') {
      fcmTokenService.cleanup()
    }
    
    this.isInitialized = false
  }
}

// Singleton instance
export const unifiedPushService = new UnifiedPushNotificationService()

// Convenience functions
export const initializePushNotifications = () => unifiedPushService.initialize()
export const requestPushPermissions = () => unifiedPushService.requestPermissions()
export const schedulePushNotification = (payload: PushNotificationPayload) => 
  unifiedPushService.scheduleNotification(payload)
export const sendInstantPushNotification = (payload: PushNotificationPayload) =>
  unifiedPushService.sendInstantNotification(payload)
export const cancelPushNotification = (id: string) => 
  unifiedPushService.cancelScheduledNotification(id)

export default unifiedPushService